import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { qs } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { brandColor, basisLabel, fmt, pct, signed } from "@/lib/utils-msip";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, LabelList } from "recharts";
import { Switch } from "@/components/ui/switch";
import { ArrowUpRight, ArrowDownRight, Minus, UploadCloud, MessageSquareText, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SummaryMatrix from "@/components/SummaryMatrix";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monLabel = (m) => (m ? `${MON[+m.slice(5, 7) - 1]} ${m.slice(2, 4)}` : m);

const Kpi = ({ label, value, sub, tone, testId, active }) => (
  <div data-testid={testId} className={`border bg-card rounded-md p-4 hover:shadow-sm hover:-translate-y-[1px] transition-transform duration-200 ${active ? "border-foreground/60 shadow-sm" : ""}`}>
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    <div className={`num text-2xl font-bold mt-1 ${tone || ""}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const MomArrow = ({ v }) =>
  v == null ? <Minus size={14} className="text-muted-foreground" /> : v > 0 ? (
    <span className="text-emerald-600 flex items-center text-xs font-semibold"><ArrowUpRight size={14} />{signed(v)} pts</span>
  ) : v < 0 ? (
    <span className="text-red-600 flex items-center text-xs font-semibold"><ArrowDownRight size={14} />{signed(v)} pts</span>
  ) : (
    <span className="text-muted-foreground text-xs">0.00</span>
  );

export default function CommandCenter() {
  const { filters } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digest, setDigest] = useState("");
  const [compare, setCompare] = useState(false);
  const [trendMode, setTrendMode] = useState("mtd");

  const openDigest = async () => {
    setDigestOpen(true);
    setDigest("");
    try {
      const { data } = await api.get(`/digest?${qs({ brand: filters.brand, basis: filters.basis })}`);
      setDigest(data.text);
    } catch {
      setDigest("Could not compose digest.");
    }
  };

  useEffect(() => {
    setLoading(true);
    api
      .get(`/dashboard?${qs({ brand: filters.brand, basis: filters.basis, period: filters.period, district: filters.district, territory_only: filters.territoryOnly })}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [filters.brand, filters.basis, filters.period, filters.district, filters.territoryOnly]);

  if (loading) return <div className="text-sm text-muted-foreground py-20 text-center">Loading command center…</div>;

  if (!data?.has_data)
    return (
      <div className="border bg-card rounded-md p-16 text-center max-w-xl mx-auto mt-10" data-testid="empty-state">
        <UploadCloud className="mx-auto text-muted-foreground" size={40} />
        <h2 className="font-heading text-lg font-semibold mt-4">No data yet</h2>
        <p className="text-sm text-muted-foreground mt-1">Upload the daily Vahan workbook (use backfill mode for the historical file) to light up the dashboard.</p>
        <Link to="/uploads" className="inline-block mt-5 rounded-md bg-foreground text-background px-4 py-2 text-sm font-semibold" data-testid="goto-uploads-btn">
          Go to Uploads
        </Link>
      </div>
    );

  const k = data.kpis;
  const periodShort = { mtd: "MTD", daily: "MTD", ytd: "YTD", last_month: "Last month" }[filters.period] || "MTD";
  return (
    <div className="space-y-6" data-testid="command-center">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">
          Command Center <span className="text-muted-foreground font-medium text-base">· {filters.brand} · {basisLabel(filters.basis)} · {periodShort}</span>
        </h1>
        <div className="flex items-center gap-3 ml-auto">
          <Button variant="outline" size="sm" onClick={openDigest} data-testid="digest-btn">
            <MessageSquareText size={14} className="mr-1.5" /> Daily Digest
          </Button>
          <span className="text-xs text-muted-foreground">Data as of {data.freshness}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi testId="kpi-mtd-share" label="MTD Share" value={pct(k.mtd_share)} sub={k.target != null ? `Target ${pct(k.target)}` : ""} active={periodShort === "MTD"} />
        <Kpi testId="kpi-ytd-share" label="YTD Share" value={pct(k.ytd_share)} sub={k.ytd_label} active={periodShort === "YTD"} />
        <Kpi testId="kpi-rank" label={`Rank (${periodShort})`} value={k.rank ? `#${k.rank}` : "—"} sub={k.period_label} />
        <Kpi testId="kpi-gap-rival" label={`Gap vs Nearest Rival (${periodShort})`} value={`${signed(k.gap_to_rival)} pts`} tone={k.gap_to_rival >= 0 ? "text-emerald-700" : "text-red-600"} sub={k.rival ? `${k.rival}: ${pct(k.rival_share)}` : "—"} />
        <Kpi testId="kpi-gap-target" label={`Gap to Target (${periodShort})`} value={k.gap_to_target != null ? `${signed(k.gap_to_target)} pts` : "—"} tone={k.gap_to_target >= 0 ? "text-emerald-700" : "text-red-600"} sub={`Unit gap: ${fmt(k.unit_gap)} units`} />
        <Kpi testId="kpi-tiv" label={`TIV (${periodShort})`} value={fmt(k.tiv)} sub={`${filters.brand}: ${fmt(k.brand_units)} units @ ${pct(k.period_share)}`} />
        <Kpi testId="kpi-projection" label="Proj. Month-end" value={fmt(k.proj_units)} sub={`@ ${pct(k.proj_share)} · ${k.elapsed_wd}/${k.total_wd} wd`} />
      </div>

      {data.daily && (
        <div className="border bg-card rounded-md p-4" data-testid="daily-strip">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Daily change · {data.daily.label}</div>
              <div className="num text-lg font-bold">
                +{fmt(data.daily.total_delta)} regs <span className="text-muted-foreground font-medium text-sm">market</span> · {" "}
                <span style={{ color: brandColor(filters.brand) }}>+{fmt(data.daily.brand_delta)} {filters.brand}</span>{" "}
                <span className="text-muted-foreground font-medium text-sm">({pct(data.daily.brand_share_of_delta)} of delta)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 ml-auto">
              {data.daily.top_rtos.map((r) => (
                <Link key={r.rto} to={`/rto/${encodeURIComponent(r.rto)}`} className="rounded border bg-secondary/60 px-2 py-1 text-xs hover:bg-secondary transition-colors duration-200">
                  {r.rto} <b className="num">+{r.delta}</b>
                </Link>
              ))}
            </div>
          </div>
          {data.daily.brand_split?.length > 0 && (
            <div className="mt-3 pt-3 border-t space-y-2" data-testid="daily-brand-split">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Day's registrations by brand</div>
              <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-secondary">
                {data.daily.brand_split.filter((s) => s.delta > 0).map((s) => (
                  <div key={s.brand} style={{ width: `${s.pct}%`, background: brandColor(s.brand) }} title={`${s.brand} +${s.delta} (${s.pct}%)`} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {data.daily.brand_split.map((s) => (
                  <span key={s.brand} className="flex items-center gap-1.5 text-xs" data-testid={`daily-split-${s.brand}`}>
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: brandColor(s.brand) }} />
                    <span className={s.brand === filters.brand ? "font-bold" : "text-muted-foreground"}>{s.brand}</span>
                    <b className="num">{s.delta > 0 ? `+${fmt(s.delta)}` : fmt(s.delta)}</b>
                    <span className="text-muted-foreground">({s.pct}%)</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border bg-card rounded-md p-4" data-testid="trend-chart" data-mode={trendMode}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-heading text-sm font-semibold" data-testid="trend-title">{trendMode === "ytd" ? "YTD share trend" : "Monthly share trend"} — {filters.brand} ({basisLabel(filters.basis)})</h3>
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-md border bg-card p-0.5 text-[11px] font-semibold" role="tablist" aria-label="Trend basis" data-testid="trend-mode">
                {[["mtd", "MTD"], ["ytd", "YTD"]].map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={trendMode === k} onClick={() => setTrendMode(k)} data-testid={`trend-mode-${k}`}
                    className={`px-2.5 py-1 rounded transition-colors ${trendMode === k ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>{label}</button>
                ))}
              </div>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                <Switch checked={compare} onCheckedChange={setCompare} data-testid="trend-compare-toggle" />
                Compare rivals
              </label>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={290}>
            <LineChart data={trendMode === "ytd" ? data.trend_ytd : data.trend} margin={{ top: 18, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e5e9f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={monLabel} />
              <YAxis tick={{ fontSize: 11 }} unit="%" domain={["auto", "auto"]} />
              <Tooltip formatter={(v) => `${v}%`} labelFormatter={monLabel} />
              {compare && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {k.target != null && (
                <ReferenceLine y={k.target} stroke="#0d765a" strokeDasharray="6 4" label={{ value: `Target ${k.target}%`, fontSize: 10, fill: "#0d765a", position: "insideBottomRight" }} />
              )}
              <Line type="monotone" dataKey={filters.brand} stroke={brandColor(filters.brand)} strokeWidth={3} dot={{ r: 4, fill: brandColor(filters.brand) }} activeDot={{ r: 5 }}>
                {!compare && <LabelList dataKey={filters.brand} position="top" formatter={(v) => `${v}%`} style={{ fontSize: 10, fontWeight: 700, fill: brandColor(filters.brand) }} />}
              </Line>
              {compare && data.trend_brands.filter((b) => b !== filters.brand && !(filters.basis === "total" && b === "MARUTI")).map((b) => (
                <Line key={b} type="monotone" dataKey={b} stroke={brandColor(b)} strokeWidth={1.5} strokeDasharray="4 3" dot={{ r: 2 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
          {trendMode === "ytd" && (
            <p className="text-[10px] text-muted-foreground mt-1" data-testid="trend-ytd-note">Each point is the cumulative financial-year share (Apr → that month) from the month's last YTD figures.</p>
          )}
          {compare && data.trend_brands.includes("MARUTI") && (
            <p className="text-[10px] text-muted-foreground mt-1">MARUTI hidden in compare mode (its ~50% share flattens the chart).</p>
          )}
        </div>

        <div className="border bg-card rounded-md overflow-hidden flex flex-col" data-testid="district-cards">
          <div className="px-4 py-3 border-b text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Districts · {periodShort}</div>
          <div className="overflow-auto divide-y max-h-[340px]">
            {(data.districts || []).filter((d) => d.tiv > 0).map((d) => (
              <div key={d.district} className="px-4 py-2 flex items-center justify-between gap-2" data-testid={`district-card-${d.district.replace(/\s/g, "-")}`}>
                <div className="min-w-0">
                  <div className="text-xs font-semibold truncate">{d.district}</div>
                  <div className="text-[11px] text-muted-foreground">TIV {fmt(d.tiv)}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="num text-sm font-bold">{pct(d.share)}</span>
                  <MomArrow v={d.mom} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {[["Top gainers vs last month", data.movers.gainers, "gainers"], ["Top losers vs last month", data.movers.losers, "losers"]].map(([title, list, key]) => (
          <div key={key} className="border bg-card rounded-md p-4" data-testid={`movers-${key}`}>
            <h3 className="font-heading text-sm font-semibold mb-2">{title} <span className="text-muted-foreground font-normal">(min TIV 100)</span></h3>
            {list.length === 0 && <p className="text-xs text-muted-foreground">None</p>}
            <div className="divide-y">
              {list.map((m) => (
                <Link key={m.rto} to={`/rto/${encodeURIComponent(m.rto)}`} className="flex items-center justify-between py-2 text-sm hover:bg-muted/50 px-1 transition-colors duration-200">
                  <span>{m.rto}</span>
                  <span className="flex items-center gap-3">
                    <span className="num text-muted-foreground">{pct(m.share)}</span>
                    <MomArrow v={m.delta} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      <SummaryMatrix />

      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-xl" data-testid="digest-dialog">
          <DialogHeader><DialogTitle className="font-heading">Daily Digest — paste into WhatsApp</DialogTitle></DialogHeader>
          <textarea
            readOnly
            value={digest || "Composing…"}
            data-testid="digest-textarea"
            className="w-full h-80 rounded-md border bg-secondary/40 p-3 text-xs font-mono resize-none focus:outline-none"
          />
          <Button
            data-testid="digest-copy-btn"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(digest);
                toast.success("Copied — paste into WhatsApp / GTMS group");
              } catch {
                try {
                  const ta = document.querySelector('[data-testid="digest-textarea"]');
                  ta.select();
                  document.execCommand("copy");
                  toast.success("Copied — paste into WhatsApp / GTMS group");
                } catch {
                  toast.error("Copy blocked by browser — select the text and copy manually");
                }
              }
            }}
            disabled={!digest}
          >
            <Copy size={14} className="mr-1.5" /> Copy to clipboard
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
